import { Suspense, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { usePageTracking } from "@/hooks/use-page-tracking";
import { useFirebaseScreenTracking } from "@/hooks/use-firebase-screen-tracking";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { isRoyaltyBrandsMarketingHost } from "@/lib/royal-brands";

// Lazy load authentication pages
const LandingPage = lazyWithRetry(() => import("@/pages/landing"));
const LoginPage = lazyWithRetry(() => import("@/pages/login"));
const FranchiseLoginPage = lazyWithRetry(() => import("@/pages/franchise-login"));
const FranchiseeRegistration = lazyWithRetry(() => import("@/pages/franchisee-registration"));
const JoinPage = lazyWithRetry(() => import("@/pages/join"));
const OfficeListPage = lazyWithRetry(() => import("@/pages/public-booking/OfficeListPage"));
const OfficeBookingPage = lazyWithRetry(() => import("@/pages/public-booking/OfficeBookingPage"));
const OfficeBookingCancelPage = lazyWithRetry(() => import("@/pages/public-booking/OfficeBookingCancelPage"));
const SiteSharePage = lazyWithRetry(() => import("@/pages/site-share/SiteSharePage"));
const CompleteRegistration = lazyWithRetry(() => import("@/pages/complete-registration"));
const PendingApproval = lazyWithRetry(() => import("@/pages/pending-approval"));
const AccountDisabled = lazyWithRetry(() => import("@/pages/account-disabled"));
const AuthStatusPage = lazyWithRetry(() => import("@/pages/auth-status").then(m => ({ default: m.AuthStatusPage })));
const DeveloperLogin = lazyWithRetry(() => import("@/pages/developer-login").then(m => ({ default: m.DeveloperLogin })));
const DeveloperPortal = lazyWithRetry(() => import("@/pages/developer-portal"));
const DemoSplash = lazyWithRetry(() => import("@/pages/demo-splash"));
const NoFranchisePage = lazyWithRetry(() => import("@/pages/no-franchise"));
const EnrollMFAPage = lazyWithRetry(() => import('@/pages/enroll-mfa'));
const RoyaltyLandingPage = lazyWithRetry(() => import("@/pages/royalty-landing"));
const EulaPage = lazyWithRetry(() => import("@/pages/legal/eula"));
const PrivacyPage = lazyWithRetry(() => import("@/pages/legal/privacy"));
const TermsPage = lazyWithRetry(() => import("@/pages/legal/terms"));

// Critical app shells use retry logic to absorb transient 429s from platform rate limits
const DeveloperApp = lazyWithRetry(() => import("@/apps/DeveloperApp"));
const FranchisorApp = lazyWithRetry(() => import("@/apps/FranchisorApp"));
const FranchiseeApp = lazyWithRetry(() => import("@/apps/FranchiseeApp"));
const AccountantApp = lazyWithRetry(() => import("@/apps/AccountantApp"));
const RoyaltyBrandApp = lazyWithRetry(() => import("@/apps/RoyaltyBrandApp"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Single source of truth for Sentry user context.
// Rendering this exactly once at the App root ensures only one instance ever
// calls Sentry.setUser / Sentry.setContext, eliminating the race condition that
// occurred when multiple useFirebaseAuth() callers each ran the effect and one
// would call Sentry.setUser(null) while another still had the user loaded.
function SentrySyncUser() {
  const { user } = useFirebaseAuth();

  useEffect(() => {
    if (user) {
      const actor = user._actor;
      if (actor) {
        // Impersonation active — record the real viewer as the Sentry user.
        Sentry.setUser({
          id: actor.id,
          email: actor.email,
          username: [actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.email || actor.id,
        });
        // Preserve the viewed-as account as Sentry context for debugging.
        Sentry.setContext('impersonation', {
          userId: user.id,
          email: user.email,
          name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
          role: user.role,
        });
      } else {
        // Normal session — no impersonation.
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
        });
        Sentry.setContext('impersonation', null);
      }
    } else {
      Sentry.setUser(null);
      Sentry.setContext('impersonation', null);
    }
  }, [user]);

  return null;
}

// Redirect component for authenticated users hitting login
function RedirectToHome() {
  const [, setLocation] = useLocation();
  const { user } = useFirebaseAuth();
  
  useEffect(() => {
    // Wait for user role before redirecting to prevent 404
    if (user?.role) {
      // Redirect developers, account managers, and sales reps to developer dashboard
      // All others go to root which uses smart landing to find first available module
      const role = (user as any).role;
      const destination = (role === 'developer' || role === 'account_manager' || role === 'sales_rep')
        ? '/developer-dashboard'
        : '/';
      setLocation(destination);
    }
  }, [user, setLocation]);
  
  return <PageLoader />;
}

function Router() {
  const { user, isLoading } = useFirebaseAuth();
  const [location, setLocation] = useLocation();
  
  // Track page views for customer health analytics
  usePageTracking({ enabled: !!user });

  // Track page views for Firebase Analytics (active users, etc.) — fires
  // regardless of auth state, matching the mobile app's screenView tracking.
  useFirebaseScreenTracking();

  // Show loading screen while checking authentication
  if (isLoading) {
    return <PageLoader />;
  }
  
  // Check if accessing developer portal
  const currentHost = window.location.hostname.toLowerCase();
  const currentPath = window.location.pathname;
  const isDeveloperAccess = currentHost === 'developer.gofranchise.io' ||
                           currentHost.startsWith('developer-') || // Replit preview URLs
                           currentHost.includes('developer.gofranchise') ||
                           currentPath === '/developer' ||
                           currentPath === '/developer-login';

  // Branding only, not enforcement: on the Royal Brands marketing domain(s),
  // show the royalties-branded landing page pre-login instead of the default
  // GoFranchise one. Login itself is unchanged — same page, same backend,
  // same session — access is governed entirely by the logged-in user's
  // isRoyaltyBrand flag, never by which domain was used to arrive here.
  const isRoyaltyBrandsMarketingDomain = isRoyaltyBrandsMarketingHost(currentHost);

  // Show landing page if not authenticated
  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route path="/enroll-mfa" component={EnrollMFAPage} />
          <Route path="/franchise/:franchiseId/login" component={FranchiseLoginPage} />
          <Route path="/register/:token" component={FranchiseeRegistration} />
          <Route path="/join/:token" component={JoinPage} />
          <Route path="/complete-registration/:token" component={CompleteRegistration} />
          <Route path="/pending-approval" component={PendingApproval} />
          <Route path="/account-disabled" component={AccountDisabled} />
          <Route path="/auth-status" component={AuthStatusPage} />
          <Route path="/developer" component={DeveloperPortal} />
          <Route path="/developer-login" component={DeveloperLogin} />
          <Route path="/demo/splash/:franchiseId" component={DemoSplash} />
          <Route path="/eula" component={EulaPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/terms" component={TermsPage} />
          <Route path="/site-share/:token" component={SiteSharePage} />
          <Route path="/book/cancel/:token" component={OfficeBookingCancelPage} />
          <Route path="/book/:token/:officeId" component={OfficeBookingPage} />
          <Route path="/book/:token" component={OfficeListPage} />
          <Route component={
            isDeveloperAccess
              ? DeveloperPortal
              : isRoyaltyBrandsMarketingDomain
                ? RoyaltyLandingPage
                : LandingPage
          } />
        </Switch>
      </Suspense>
    );
  }

  // Route to appropriate application layer based on user role
  // All franchisor staff roles (not franchisee) use FranchisorApp
  let AppLayer;
  const userRole = (user as any).role;
  
  // Debug logging for role detection
  
  const hasFranchise = !!(user as any).franchiseId;

  if (userRole === 'developer' || userRole === 'account_manager' || userRole === 'sales_rep') {
    AppLayer = DeveloperApp;
  } else if ((user as any).isRoyaltyBrand) {
    // Royal Brand users get the restricted royalties-only shell regardless of
    // their underlying role (franchisee/manager/etc.) — the franchise-level
    // flag is the sole source of truth for access, not the role or the domain
    // they logged in from. Checked before the generic role branches below so
    // it can never fall through to a fuller app layer.
    AppLayer = RoyaltyBrandApp;
  } else if (!hasFranchise) {
    // Redirect users without a franchise to the no-franchise page
    // But only if they aren't already on it to avoid loops
    if (location !== '/no-franchise') {
      // Use timeout to avoid state update during render
      setTimeout(() => setLocation('/no-franchise'), 0);
      return <PageLoader />;
    }
    AppLayer = () => null; // No app layer needed
  } else if (userRole === 'accountant') {
    // Accountants get their own dedicated app layer
    AppLayer = AccountantApp;
  } else if (userRole === 'franchisee') {
    AppLayer = FranchiseeApp;
  } else {
    // All other roles are franchisor staff
    AppLayer = FranchisorApp;
  }
  
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          {/* Special routes that don't use the app layout */}
          <Route path="/demo/splash/:franchiseId" component={DemoSplash} />
          <Route path="/no-franchise" component={NoFranchisePage} />
          {/* Public legal pages — must remain accessible for QBO compliance */}
          <Route path="/eula" component={EulaPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/terms" component={TermsPage} />
          {/* Redirect authenticated users away from auth-only pages */}
          <Route path="/login" component={RedirectToHome} />
          <Route path="/franchise/:franchiseId/login" component={RedirectToHome} />
          <Route path="/register/:token" component={RedirectToHome} />
          <Route path="/complete-registration/:token" component={RedirectToHome} />
          <Route path="/pending-approval" component={RedirectToHome} />
          <Route path="/account-disabled" component={RedirectToHome} />
          <Route path="/auth-status" component={RedirectToHome} />
          <Route path="/developer-login" component={RedirectToHome} />
          <Route path="/developer" component={RedirectToHome} />
          <Route path="/join/:token" component={JoinPage} />
          {/* Locked share page: standalone (no app shell) for signed-in staff previewing a link too */}
          <Route path="/site-share/:token" component={SiteSharePage} />
          <Route path="/book/cancel/:token" component={OfficeBookingCancelPage} />
          {/* Note: /book/:token and /book/:token/:officeId are intentionally
              NOT registered here — a logged-in user hitting those URLs
              should see them inside the app shell (sidebar/header), so
              each AppLayer registers its own copy of these routes wrapped
              in Layout. Only the unauthenticated Switch above keeps the
              standalone versions, for anonymous visitors. */}
          <Route component={AppLayer} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="gf-theme"
    >
      <QueryClientProvider client={queryClient}>
        {/* Single Sentry user sync — must live outside Router so it is never
            unmounted on navigation, preventing races from multiple hook instances */}
        <SentrySyncUser />
        <TooltipProvider>
          <ErrorBoundary level="root">
            <Router />
          </ErrorBoundary>
          <Toaster />
          {/* Required for Firebase phone auth reCAPTCHA — must be always mounted, never inside Router */}
          <div id="recaptcha-container" />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
